import React, { useState, useRef, useEffect } from 'react';
import { CHAT_FAQS } from '../data/siteData';

const WELCOME = {
  id: 'w0',
  role: 'bot',
  text: "👋 Hi! I'm **JSBot**, your JSDevBrains assistant.\n\nI can answer questions about our services, products, pricing, and team. How can I help you today?",
  time: new Date(),
};

function formatTime(d) {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function parseBold(text) {
  // Convert **text** to <strong>
  return text.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
    part.startsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );
}

function MsgBubble({ msg }) {
  const lines = msg.text.split('\n');
  return (
    <div className={`msg ${msg.role}`}>
      {msg.role === 'bot' && (
        <div className="msg-av" aria-hidden="true">🤖</div>
      )}
      <div>
        <div className="msg-bubble">
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {parseBold(line)}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
        <div className={`msg-time ${msg.role === 'user' ? 'text-right' : ''}`}>
          {formatTime(msg.time)}
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="msg bot">
      <div className="msg-av" aria-hidden="true">🤖</div>
      <div>
        <div className="msg-bubble" aria-label="JSBot is typing">
          <div className="typing-dots">
            <span /><span /><span />
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple keyword matcher
function getAutoResponse(input) {
  const q = input.toLowerCase();
  for (const faq of CHAT_FAQS) {
    const keywords = faq.q.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ');
    const matches = keywords.filter((k) => k.length > 3 && q.includes(k)).length;
    if (matches >= 1) return faq.a;
  }
  // Fallback
  if (q.includes('price') || q.includes('cost') || q.includes('rate') || q.includes('charge')) {
    return CHAT_FAQS[1].a;
  }
  if (q.includes('start') || q.includes('begin') || q.includes('quick') || q.includes('fast')) {
    return CHAT_FAQS[2].a;
  }
  if (q.includes('location') || q.includes('where') || q.includes('address') || q.includes('thanjavur')) {
    return CHAT_FAQS[7].a;
  }
  if (q.includes('service') || q.includes('offer') || q.includes('do you')) {
    return CHAT_FAQS[0].a;
  }
  return "I'm not sure I have the answer to that specific question. For the most accurate response, please:\n• **WhatsApp us** at +91 81236 29993 (fastest!)\n• Or use the **Contact form** on this page.\n\nWe respond within minutes on WhatsApp! 💬";
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const msgsRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [messages, typing]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setUnread(0);
    }
  }, [open]);

  const sendMessage = (text) => {
    if (!text.trim()) return;

    const userMsg = { id: Date.now(), role: 'user', text: text.trim(), time: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTyping(true);

    // Simulate bot thinking
    const delay = 800 + Math.random() * 700;
    setTimeout(() => {
      const response = getAutoResponse(text);
      const botMsg = { id: Date.now() + 1, role: 'bot', text: response, time: new Date() };
      setMessages((prev) => [...prev, botMsg]);
      setTyping(false);
      if (!open) setUnread((n) => n + 1);
    }, delay);
  };

  const handleFaqClick = (faq) => {
    sendMessage(faq.q);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="chat-widget" aria-label="Chat widget">
      {/* Chat window */}
      {open && (
        <div className="chat-window" role="dialog" aria-label="Chat with JSBot" aria-modal="true">
          {/* Header */}
          <div className="chat-head">
            <div className="chat-head-av" aria-hidden="true">🤖</div>
            <div className="chat-head-info">
              <div className="chat-head-name">JSBot — JSDevBrains Assistant</div>
              <div className="chat-head-status">Online · AI-powered</div>
            </div>
            <button className="chat-head-close" onClick={() => setOpen(false)} aria-label="Close chat">✕</button>
          </div>

          {/* Messages */}
          <div className="chat-msgs" ref={msgsRef} role="log" aria-live="polite" aria-label="Chat messages">
            {messages.map((msg) => (
              <MsgBubble key={msg.id} msg={msg} />
            ))}
            {typing && <TypingIndicator />}
          </div>

          {/* Quick FAQ chips */}
          <div className="chat-faqs" aria-label="Quick question suggestions">
            {CHAT_FAQS.slice(0, 6).map((faq) => (
              <button
                key={faq.q}
                className="chat-faq-btn"
                onClick={() => handleFaqClick(faq)}
                title={faq.q}
              >
                {faq.q.length > 28 ? faq.q.slice(0, 27) + '…' : faq.q}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="chat-input-row">
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Type your message"
              maxLength={400}
            />
            <button
              className="chat-send"
              onClick={() => sendMessage(input)}
              aria-label="Send message"
              disabled={!input.trim()}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* Bubble toggle */}
      <button
        className="chat-bubble"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
      >
        {open ? '✕' : '💬'}
        {!open && unread > 0 && (
          <span className="chat-unread" aria-label={`${unread} unread messages`}>{unread}</span>
        )}
      </button>
    </div>
  );
}
